import React from "react";

import styled from "styled-components";

import { colorTheme } from "@utils";

import AdvancedSelect from "./AdvancedSelect/AdvancedSelect";
import SingleSelect from "./SingleSelect";
import type { OptionProp, SelectProps, ValueProps } from "./types";
import { getMargins } from "../utilities/margin";

/*
Select new prop names 
    isDisabled -> disabled
    isCreatable -> creatable
    isLoading -> loading
    isMulti -> multiValues
*/

const Label = styled.label`
  margin-bottom: 0;
`;

const HelperText = styled.div`
  width: 100%;
  margin-left: 5px;
  font-size: 14px;
`;

const RequiredSpan = styled.span`
  color: ${colorTheme("danger")};
  font-size: 14px;
  margin-left: 1px;
`;

const SelectComponent = <TValue extends ValueProps>({
  label,
  required = false,
  style = {},
  labelStyle = {},
  requiredStyle = {},
  helperTextStyle = {},
  helperText,
  m,
  mx,
  my,
  mt,
  mb,
  mr,
  ml,
  ...props
}: SelectProps<TValue>) => {
  // Set deprecated props to new props
  props.disabled = props.isDisabled ?? props.disabled;
  props.creatable = props.isCreatable ?? props.creatable;
  props.loading = props.isLoading ?? props.loading;
  props.multiValues = props.isMulti ?? props.multiValues;

  style = { ...getMargins({ m, mx, my, mt, mb, mr, ml }), ...style };

  const getSelectValue = () => {
    let _selectValue = null;

    const consolidatedOptions: OptionProp[] = [];

    const findOptions = (o: OptionProp[]) => {
      o.forEach((option) => {
        if (option) {
          if (option.options) {
            findOptions(option.options);
          } else {
            consolidatedOptions.push(option);
          }
        }
      });
    };

    if (props.options) {
      findOptions(props.options);
    }

    if (props.options || props.creatable) {
      if (Array.isArray(props.value)) {
        _selectValue = props.value.map((_value) => {
          const find = consolidatedOptions.find(
            (option) => option.value === _value,
          );
          if (find) {
            return { value: `${find.value}`, label: `${find.label}` };
          } else {
            return { label: _value, value: _value };
          }
        });
      } else if (props.options && props.value) {
        const find = consolidatedOptions.find(
          (option) => option.value === props.value,
        );
        if (find) {
          _selectValue = { value: `${find.value}`, label: `${find.label}` };
        } else {
          _selectValue = {
            value: props.value.toString(),
            label: props.value.toString(),
          };
        }
      }

      if (_selectValue === undefined) {
        _selectValue = null;
      }
    }

    return _selectValue;
  };

  // Find Value
  const selectValue = getSelectValue();

  let Component = null;

  const useAdvancedSelect =
    props.useLatestSelect ||
    ["breadcrumbs", "accordion", "popout"].includes(`${props.type}`) ||
    props.customComponent ||
    props.selectedValuesAtTop;

  if (props.creatable && props.useLatestSelect) {
    console.error(
      "Creatable is not supported yet using latest select (not react-select)",
    );
  }

  if (!props.creatable && useAdvancedSelect) {
    Component = AdvancedSelect;
  } else {
    Component = SingleSelect;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", ...style }}>
      {label && !props.customComponent && (
        <Label
          style={{
            color: props.error ? colorTheme("danger") : colorTheme("black"),
            ...labelStyle,
          }}
        >
          {label}
          {required && <RequiredSpan style={requiredStyle}>*</RequiredSpan>}
        </Label>
      )}
      <Component {...props} value={selectValue} label={label} />
      {props.error ? (
        <HelperText style={{ color: colorTheme("danger"), ...helperTextStyle }}>
          {props.error}
        </HelperText>
      ) : helperText ? (
        <HelperText style={helperTextStyle}>{helperText}</HelperText>
      ) : null}
    </div>
  );
};
export default SelectComponent;
